export interface UserXP {
  userId: string;
  totalXP: number;
  level: number;
  currentLevelXP: number;
  nextLevelXP: number;
  lastUpdated: Date;
}

export interface XPTransaction {
  id: string;
  userId: string;
  amount: number;
  source: 'match' | 'tournament' | 'achievement' | 'daily' | 'bonus';
  sourceId?: string;
  description: string;
  timestamp: Date;
  metadata?: {
    matchResult?: 'win' | 'loss' | 'draw';
    tournamentPosition?: number;
    achievementId?: string;
  };
}

export interface LevelConfig {
  level: number;
  requiredXP: number;
  title: string;
  rewards?: {
    type: 'badge' | 'title' | 'avatar' | 'bonus';
    value: string;
  }[];
}

export interface XPSource {
  type: 'match' | 'tournament' | 'achievement' | 'daily';
  baseXP: number;
  multipliers?: {
    condition: string;
    value: number;
  }[];
}

export const XP_SOURCES: Record<string, XPSource> = {
  MATCH_WIN: {
    type: 'match',
    baseXP: 30,
    multipliers: [
      { condition: 'perfect_game', value: 1.67 },
      { condition: 'comeback', value: 1.3 }
    ]
  },
  MATCH_LOSS: {
    type: 'match',
    baseXP: 10
  },
  MATCH_DRAW: {
    type: 'match',
    baseXP: 20
  },
  TOURNAMENT_1ST: {
    type: 'tournament',
    baseXP: 500
  },
  TOURNAMENT_2ND: {
    type: 'tournament',
    baseXP: 300
  },
  TOURNAMENT_3RD: {
    type: 'tournament',
    baseXP: 200
  },
  TOURNAMENT_TOP10: {
    type: 'tournament',
    baseXP: 100
  },
  TOURNAMENT_PARTICIPATION: {
    type: 'tournament',
    baseXP: 50
  },
  DAILY_LOGIN: {
    type: 'daily',
    baseXP: 10
  },
  FIRST_MATCH_OF_DAY: {
    type: 'daily',
    baseXP: 20
  }
};

export const LEVEL_CONFIGS: LevelConfig[] = [
  { level: 1, requiredXP: 0, title: 'Kezdő' },
  { level: 2, requiredXP: 100, title: 'Tanuló' },
  { level: 3, requiredXP: 250, title: 'Gyakorló' },
  { level: 4, requiredXP: 500, title: 'Tapasztalt' },
  { level: 5, requiredXP: 1000, title: 'Haladó' },
  { level: 6, requiredXP: 1750, title: 'Szakértő' },
  { level: 7, requiredXP: 2750, title: 'Mester' },
  { level: 8, requiredXP: 4000, title: 'Nagymester' },
  { level: 9, requiredXP: 5500, title: 'Bajnok' },
  { level: 10, requiredXP: 7500, title: 'Legenda' },
  { level: 11, requiredXP: 10000, title: 'Elit' },
  { level: 12, requiredXP: 13000, title: 'Profi' },
  { level: 13, requiredXP: 16500, title: 'Virtuóz' },
  { level: 14, requiredXP: 20500, title: 'Titán' },
  { level: 15, requiredXP: 25000, title: 'Félisten' },
  { level: 16, requiredXP: 30000, title: 'Isten' },
  { level: 17, requiredXP: 36000, title: 'Halhatatlan' },
  { level: 18, requiredXP: 43000, title: 'Kozmikus' },
  { level: 19, requiredXP: 51000, title: 'Transzcendens' },
  { level: 20, requiredXP: 60000, title: 'Abszolút' }
];

export function calculateLevel(totalXP: number): { level: number; currentLevelXP: number; nextLevelXP: number; title: string } {
  let level = 1;
  let currentLevelXP = 0;
  let nextLevelXP = LEVEL_CONFIGS[1].requiredXP;

  for (let i = LEVEL_CONFIGS.length - 1; i >= 0; i--) {
    if (totalXP >= LEVEL_CONFIGS[i].requiredXP) {
      level = LEVEL_CONFIGS[i].level;
      currentLevelXP = totalXP - LEVEL_CONFIGS[i].requiredXP;
      
      if (i < LEVEL_CONFIGS.length - 1) {
        nextLevelXP = LEVEL_CONFIGS[i + 1].requiredXP - LEVEL_CONFIGS[i].requiredXP;
      } else {
        nextLevelXP = 0;
      }
      
      return {
        level,
        currentLevelXP,
        nextLevelXP,
        title: LEVEL_CONFIGS[i].title
      };
    }
  }

  return {
    level: 1,
    currentLevelXP: totalXP,
    nextLevelXP: LEVEL_CONFIGS[1].requiredXP,
    title: LEVEL_CONFIGS[0].title
  };
}

export function calculateXPForMatch(result: 'win' | 'loss' | 'draw', metadata?: any): number {
  let xp = 0;

  switch (result) {
    case 'win':
      xp = XP_SOURCES.MATCH_WIN.baseXP;
      if (metadata?.perfectGame) {
        xp *= XP_SOURCES.MATCH_WIN.multipliers?.[0].value || 1;
      }
      break;
    case 'loss':
      xp = XP_SOURCES.MATCH_LOSS.baseXP;
      break;
    case 'draw':
      xp = XP_SOURCES.MATCH_DRAW.baseXP;
      break;
  }

  return Math.round(xp);
}

export function calculateXPForTournament(position: number, totalParticipants: number): number {
  if (position === 1) {
    return XP_SOURCES.TOURNAMENT_1ST.baseXP;
  } else if (position === 2) {
    return XP_SOURCES.TOURNAMENT_2ND.baseXP;
  } else if (position === 3) {
    return XP_SOURCES.TOURNAMENT_3RD.baseXP;
  } else if (position <= 10) {
    return XP_SOURCES.TOURNAMENT_TOP10.baseXP;
  } else {
    return XP_SOURCES.TOURNAMENT_PARTICIPATION.baseXP;
  }
}
